import axiosInstance from "api"
import { Endpoint } from "api/enpoints.types"
import { AxiosError, AxiosResponse } from "axios"
import qs from "qs"
import { useQuery } from "react-query"

import {
  IExportClassJobsTaskArgs,
  IExportClassStudentsArgs,
  IExportClassStoreTransactionsTaskArgs,
  IExportGroupsTaskArgs,
  IExportGroupStoreTransactionsTaskArgs,
  IExportReportByIdArgs,
  IExportReportDownloadArgs,
  IExportReportDownloadResponse,
  IExportReportResponse,
  IExportSchoolGroupsTaskArgs,
  IExportStudentJobsTaskArgs,
  IExportStudentStoreTransactionsTaskArgs,
  IExportStudentTransactionsTaskArgs,
  IExportTaskResponse,
  IExportTeacherTransactionsArgs,
} from "./export.types"

export const useQueryExportClassJobsTask = (args: IExportClassJobsTaskArgs) =>
  useQuery<AxiosResponse<IExportTaskResponse>, AxiosError>(
    ["exportClassJobsTask", args.classId],
    async () =>
      axiosInstance(
        Endpoint.CLASSES_ID_JOBS_EXPORT.replace("ID", `${args.classId}`)
      ),
    args?.options
  )

export const useQueryExportTeacherTransactions = (
  args: IExportTeacherTransactionsArgs
) =>
  useQuery<AxiosResponse<IExportTaskResponse>, AxiosError>(
    ["exportTeacherTransactions"],
    async () =>
      axiosInstance(Endpoint.TEACHER_TRANSACTIONS_EXPORT, {
        params: {
          classes: args.classes,
          group_ids: args.group_ids,
          type_in: args.type_in,
          date_from: args.date_from,
          date_to: args.date_to,
          title_filter: args.title_filter,
          student_filter: args.student_filter,
          sender_type: args.sender_type,
        },
        paramsSerializer: (params) => {
          return qs.stringify(params, { indices: false })
        },
      }),
    args?.options
  )

export const useQueryExportClassStudents = (args: IExportClassStudentsArgs) =>
  useQuery<AxiosResponse<IExportTaskResponse>, AxiosError>(
    ["exportClassStudents", args.classId],
    async () =>
      axiosInstance(
        Endpoint.CLASSES_ID_STUDENTS_EXPORT.replace("ID", `${args.classId}`)
      ),
    args?.options
  )

export const useQueryExportClassStoreTransactionsTask = (
  args: IExportClassStoreTransactionsTaskArgs
) =>
  useQuery<AxiosResponse<IExportTaskResponse>, AxiosError>(
    ["exportClassStoreTransactionsTask", args.classId],
    async () =>
      axiosInstance(
        Endpoint.CLASSES_ID_STORE_TRANSACTIONS_EXPORT.replace(
          "ID",
          `${args.classId}`
        )
      ),
    args?.options
  )

// No `group_ids`: the admin Grouping page has no filter or selection, so the export
// always covers every school-wide group.
export const useQueryExportSchoolGroupsTask = (
  args: IExportSchoolGroupsTaskArgs
) =>
  useQuery<AxiosResponse<IExportTaskResponse>, AxiosError>(
    ["exportSchoolGroupsTask"],
    async () => axiosInstance(Endpoint.TEACHER_ADMIN_SCHOOL_GROUPS_EXPORT),
    args?.options
  )

export const useQueryExportGroupsTask = (args: IExportGroupsTaskArgs) =>
  useQuery<AxiosResponse<IExportTaskResponse>, AxiosError>(
    ["exportGroupsTask", args.classId, args.group_ids],
    async () =>
      axiosInstance(
        Endpoint.CLASSES_ID_GROUPS_EXPORT.replace("CLASSID", `${args.classId}`),
        {
          params: { group_ids: args.group_ids },
          paramsSerializer: (params) => qs.stringify(params, { indices: false }),
        }
      ),
    args?.options
  )

export const useQueryExportGroupStoreTransactionsTask = (
  args: IExportGroupStoreTransactionsTaskArgs
) =>
  useQuery<AxiosResponse<IExportTaskResponse>, AxiosError>(
    ["exportGroupStoreTransactionsTask", args.classId, args.group_ids],
    async () =>
      axiosInstance(
        Endpoint.CLASSES_ID_GROUPS_STORE_EXPORT.replace(
          "CLASSID",
          `${args.classId}`
        ),
        {
          params: { group_ids: args.group_ids },
          paramsSerializer: (params) => qs.stringify(params, { indices: false }),
        }
      ),
    args?.options
  )

export const useQueryExportStudentJobsTask = (
  args: IExportStudentJobsTaskArgs
) =>
  useQuery<AxiosResponse<IExportTaskResponse>, AxiosError>(
    ["exportStudentJobsTask", args.studentId],
    async () =>
      axiosInstance(
        Endpoint.STUDENTS_ID_JOBS_EXPORT.replace("ID", `${args.studentId}`)
      ),
    args?.options
  )

export const useQueryExportStudentTransactionsTask = (
  args: IExportStudentTransactionsTaskArgs
) =>
  useQuery<AxiosResponse<IExportTaskResponse>, AxiosError>(
    ["exportStudentTransactionsTask", args.studentId],
    async () =>
      axiosInstance(
        Endpoint.STUDENTS_ID_TRANSACTIONS_EXPORT.replace(
          "ID",
          `${args.studentId}`
        )
      ),
    args?.options
  )

export const useQueryExportStudentStoreTransactionsTask = (
  args: IExportStudentStoreTransactionsTaskArgs
) =>
  useQuery<AxiosResponse<IExportTaskResponse>, AxiosError>(
    ["exportStudentStoreTransactionsTask", args.classId, args.studentId],
    async () =>
      axiosInstance(
        Endpoint.CLASSES_ID_STORE_TRANSACTIONS_EXPORT_STUDENTID.replace(
          "CLASSID",
          `${args.classId}`
        ).replace("STUDENTID", `${args.studentId}`)
      ),
    args?.options
  )

export const useQueryExportReportById = (args: IExportReportByIdArgs) =>
  useQuery<AxiosResponse<IExportReportResponse>, AxiosError>(
    ["exportReport", args.exportReportId],
    async () =>
      axiosInstance(
        Endpoint.EXPORT_REPORTS_ID.replace("ID", `${args.exportReportId}`)
      ),
    {
      enabled: !!args.exportReportId,
      ...args?.options,
    }
  )

export const useQueryExportReportDownload = (
  args: IExportReportDownloadArgs
) =>
  useQuery<AxiosResponse<IExportReportDownloadResponse>, AxiosError>(
    ["exportReportDownload", args.exportReportId],
    async () =>
      axiosInstance(
        Endpoint.EXPORT_REPORTS_ID_DOWNLOAD.replace(
          "ID",
          `${args.exportReportId}`
        )
      ),
    {
      enabled: !!args.exportReportId,
      ...args?.options,
    }
  )
